import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import NewProgrammeDialog from "../components/training/NewProgrammeDialog";
import { TableRowsSkeleton } from "../components/skeleton/Skeleton";
import ProgrammeDetailsPanel from "../components/training/ProgrammeDetailsPanel";
import { selectIsOrgOwnerOrAdmin } from "../features/authentication/authenticationSlice";
import { selectProgrammes, selectSelectedProgramme } from "../features/training/training-slice";
import { fetchProgramme, fetchProgrammes } from "../features/training/slice-extensions/training-slice-extension";
import { formatDateOnly } from "../features/training/utils/formatDateOnly";
import { selectOrganization } from "../features/organization/organization-slice";
import { OrganizationKind } from "../features/organization/enums/OrganizationKind";

/**
 * The organization's programmes: a course or level plan of its own, or (a driving
 * school) a copy of a licence-class template; then adjust its topics. Every instructor can read it; only the Owner or an Admin edits.
 */
const ProgrammesSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const canManage = useAppSelector(selectIsOrgOwnerOrAdmin);
  const programmes = useAppSelector(selectProgrammes);
  const selected = useAppSelector(selectSelectedProgramme);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Until the first answer arrives an empty list means "not loaded yet", never "none yet".
  const [fetched, setFetched] = useState(false);
  const isDrivingSchool = useAppSelector(selectOrganization)?.kind === OrganizationKind.Fahrschule;

  useEffect(() => {
    dispatch(fetchProgrammes()).finally(() => setFetched(true));
  }, [dispatch]);

  useEffect(() => {
    if (selectedId === null && programmes.length > 0) setSelectedId(programmes[0].id);
  }, [programmes, selectedId]);

  useEffect(() => {
    if (selectedId) dispatch(fetchProgramme(selectedId));
  }, [dispatch, selectedId]);

  return (
    <div className="flex h-full gap-4">
      <LeftMenu />
      <div className="w-full min-w-0 p-4 pb-10 lg:px-10">
        <div className="lg:max-w-[1100px]">
          <Breadcrumbs />
          <div className="py-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1>{t("training.programmes.title")}</h1>
              {canManage && (
                <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                  <Plus size={18} strokeWidth={2} />
                  <span>
                    {isDrivingSchool ? t("training.programmes.newFromTemplate") : t("training.programmes.newProgramme")}
                  </span>
                </button>
              )}
            </div>
            <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">{isDrivingSchool ? t("training.programmes.subtitle") : t("training.programmes.subtitleCourses")}</p>
            {!canManage && (
              <p className="mt-2 text-sm text-grey-30 dark:text-grey-80">{t("training.programmes.readOnly")}</p>
            )}

            <div className="mt-4 grid grid-cols-1 gap-4 lg:mt-6 lg:grid-cols-[280px_1fr] lg:gap-6">
              <ul className="space-y-2">
                {programmes.length === 0 &&
                  (fetched ? (
                    <li className="text-sm text-grey-30 dark:text-grey-70">{t("training.programmes.empty")}</li>
                  ) : (
                    <li>
                      <TableRowsSkeleton rows={2} />
                    </li>
                  ))}
                {programmes.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(p.id)}
                      className={`w-full rounded-xl border px-4 py-3 text-left transition-colors border-grey-90 dark:border-primary-20 hover:bg-grey-95 dark:hover:bg-primary-20 ${
                        p.id === selectedId ? "bg-primary-95 dark:bg-[#3E334D]" : "bg-white dark:bg-primary-10"
                      }`}
                    >
                      <span className="block font-medium text-grey-10 dark:text-white">{p.name}</span>
                      <span className="block text-xs text-grey-50 dark:text-grey-70">
                        {[
                          p.templateVersion
                            ? t("training.programmes.templateVersion", {
                                date: formatDateOnly(p.templateVersion, i18n.language),
                              })
                            : p.licenceClass,
                          t("training.programmes.enrolledCount", { count: p.enrolledCount }),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                        {p.isArchived && ` · ${t("training.programmes.archived")}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {selected && selected.id === selectedId ? (
                <ProgrammeDetailsPanel programme={selected} canManage={canManage} />
              ) : (
                programmes.length > 0 && (
                  <p className="text-sm text-grey-30 dark:text-grey-70">{t("training.programmes.selectPrompt")}</p>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      {creating && (
        <NewProgrammeDialog
          onClose={() => setCreating(false)}
          onCreated={(programme) => {
            setCreating(false);
            setSelectedId(programme.id);
          }}
        />
      )}
    </div>
  );
};

export default ProgrammesSettingsPage;
